import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  MessageCircle, 
  Plus, 
  Search,
  Filter,
  Clock, 
  User,
  CheckCircle, 
  XCircle,
  Upload,
  Paperclip,
  Send,
  Eye,
  X,
  Trash2,
  Bot,
  Sparkles
} from 'lucide-react';
import { collection, getDocs, addDoc, updateDoc, doc, deleteDoc, query, where } from 'firebase/firestore';
import { db } from '../../firebase/config';
import BookmarkButton from '../../components/BookmarkButton';
import FileUpload, { useFileUpload } from '../../components/FileUpload';
import { formatTimestamp } from '../../utils/timestampUtils';
import MahStryAITutor from '../../components/doubts/MahStryAITutor';
import { getAdminSetting } from '../../firebase/collections';
import { Lock, Info } from 'lucide-react';
import { useRealtime } from '../../contexts/RealtimeContext';
import toast from 'react-hot-toast';

const Doubts = () => {
  const { currentUser, userProfile } = useRealtime();
  const [doubts, setDoubts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterSubject, setFilterSubject] = useState('all');
  const [showAskModal, setShowAskModal] = useState(false);
  const [selectedDoubt, setSelectedDoubt] = useState(null);
  const [newDoubt, setNewDoubt] = useState({
    subject: '',
    title: '',
    description: ''
  });
  const [error, setError] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [doubtToDelete, setDoubtToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showMahStryAITutor, setShowMahStryAITutor] = useState(false);
  const [aiTutorEnabled, setAiTutorEnabled] = useState(false);
  const [showAiTutorInfo, setShowAiTutorInfo] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const {
    uploadedFiles,
    uploading,
    progress,
    uploadFile,
    removeFile,
    setUploadedFiles
  } = useFileUpload({ storagePath: 'doubt-attachments' });

  // Load doubts from Firebase
  useEffect(() => {
    loadDoubtsFromFirebase();
    loadAiTutorSetting();
  }, [currentUser]);

  // Load AI Tutor setting from admin settings
  const loadAiTutorSetting = async () => {
    try {
      const result = await getAdminSetting('aiTutorEnabled');
      if (result.success && result.data) {
        setAiTutorEnabled(result.data.value === true);
      } else {
        // Default to locked (false) if setting doesn't exist
        setAiTutorEnabled(false);
      }
    } catch (error) {
      // Default to locked
      setAiTutorEnabled(false);
    }
  };

  const loadDoubtsFromFirebase = async () => {
    try {
      setLoading(true);
      setError(''); // Clear previous errors
      
      if (!currentUser) {
        setDoubts([]);
        setLoading(false);
        return;
      }
      
      // Load only the current user's doubts from Firebase
      const doubtsRef = collection(db, 'doubts');
      const q = query(
        doubtsRef,
        where('userId', '==', currentUser.uid)
      );
      
      const doubtsSnapshot = await getDocs(q);
      const doubtsData = doubtsSnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          // Ensure attachments array exists
          attachments: data.attachments || []
        };
      });

      // Sort by creation date (newest first)
      doubtsData.sort((a, b) => {
        const dateA = new Date(a.createdAt || 0);
        const dateB = new Date(b.createdAt || 0);
        return dateB - dateA;
      });

      setDoubts(doubtsData);
      console.log('Loaded doubts:', doubtsData.length);
    } catch (error) {
      console.error('Error loading doubts:', error);
      setError('Failed to load doubts. Please try again later.');
      setDoubts([]);
    } finally {
      setLoading(false);
    }
  };

  // Handle file upload for doubt attachments
  const handleDoubtAttachments = (uploadedFiles) => {
    // Store uploaded files for submission
    setUploadedFiles(uploadedFiles);
  };

  // Delete doubt and all associated data
  const deleteDoubt = async () => {
    if (!doubtToDelete) return;

    setDeleting(true);
    try {
      // Delete the doubt document (this will cascade delete all associated answers)
      await deleteDoc(doc(db, 'doubts', doubtToDelete.id));

      // Update local state
      setDoubts(prev => prev.filter(doubt => doubt.id !== doubtToDelete.id));
      
      // Close modals
      setShowDeleteModal(false);
      setSelectedDoubt(null);
      setDoubtToDelete(null);
    } catch (error) {
      setError('Failed to delete doubt. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  // Handle delete confirmation
  const handleDeleteDoubt = (doubt) => {
    setDoubtToDelete(doubt);
    setShowDeleteModal(true);
  };

  // Submit new doubt
  const submitDoubt = async () => {
    // Prevent double submission
    if (submitting) {
      console.log('Already submitting, please wait...');
      return;
    }

    // Validation with user feedback
    if (!newDoubt.title.trim()) {
      toast.error('Please enter a title for your doubt');
      return;
    }
    
    if (!newDoubt.description.trim()) {
      toast.error('Please enter a description for your doubt');
      return;
    }
    
    if (!newDoubt.subject) {
      toast.error('Please select a subject');
      return;
    }

    // Check if user is authenticated
    if (!currentUser) {
      toast.error('Please log in to submit a doubt');
      return;
    }

    setSubmitting(true);
    console.log('Submitting doubt with files:', uploadedFiles);
    
    try {
      // Get user display name
      const authorName = currentUser.displayName || 
                        userProfile?.name || 
                        userProfile?.displayName || 
                        currentUser.email?.split('@')[0] || 
                        'Anonymous User';

      // Extract only serializable data from uploaded files (exclude File objects)
      const attachments = (uploadedFiles || []).map(fileData => ({
        name: fileData.metadata?.name || fileData.name,
        url: fileData.url,
        path: fileData.path,
        size: fileData.metadata?.size || fileData.size,
        type: fileData.metadata?.type || fileData.type,
        uploadedAt: fileData.metadata?.uploadedAt || new Date().toISOString()
      }));

      console.log('Processed attachments:', attachments);

      const doubtData = {
        ...newDoubt,
        status: 'pending',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        author: authorName,
        authorId: currentUser.uid,
        userId: currentUser.uid, // Also include userId for security rules compatibility
        authorEmail: currentUser.email || '',
        expert: null,
        attachments: attachments,
        answers: []
      };

      console.log('Creating doubt with data:', doubtData);
      const docRef = await addDoc(collection(db, 'doubts'), doubtData);
      console.log('Doubt created successfully with ID:', docRef.id);

      // Show success message
      toast.success('Doubt submitted successfully!');

      // Reset form
      setNewDoubt({
        subject: '',
        title: '',
        description: ''
      });
      setUploadedFiles([]);
      setShowAskModal(false);

      // Reload doubts
      console.log('Reloading doubts...');
      await loadDoubtsFromFirebase();
    } catch (error) {
      console.error('Error submitting doubt:', error);
      toast.error(error.message || 'Failed to submit doubt. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Filter doubts
  const filteredDoubts = doubts.filter(doubt => {
    const matchesSearch = doubt.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         doubt.description.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = filterStatus === 'all' || doubt.status === filterStatus;
    const matchesSubject = filterSubject === 'all' || doubt.subject === filterSubject;
    
    return matchesSearch && matchesStatus && matchesSubject;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'answered': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'closed': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'answered': return <CheckCircle className="w-4 h-4" />;
      case 'pending': return <Clock className="w-4 h-4" />;
      case 'closed': return <XCircle className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  const subjects = ['Mathematics', 'Physics', 'Chemistry', 'English', 'Biology', 'Computer Science'];
  const statuses = ['all', 'pending', 'answered', 'closed'];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 pb-4 sm:pb-6 pt-2">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0">
        <div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">My Doubts</h1>
              <p className="text-xs sm:text-base text-gray-600 dark:text-gray-400">Get help with your questions from experts</p>
        </div>
            
          <div className="flex items-center gap-2 self-start sm:self-auto">
            {/* MahStry AI Tutor button */}
            <button 
                onClick={() => setShowMahStryAITutor(true)}
                className="px-3 sm:px-4 py-1.5 sm:py-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white text-xs sm:text-sm rounded-lg hover:from-purple-700 hover:to-indigo-700 flex items-center justify-center shadow-md transition-all duration-200 relative"
            >
                <Bot className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5 sm:mr-2" />
                <span>MahStry AI Tutor</span>
                <Sparkles className="w-2.5 h-2.5 sm:w-3 sm:h-3 ml-1.5 animate-pulse" />
            </button>
            <button 
                onClick={() => setShowAskModal(true)}
                className="px-3 sm:px-4 py-1.5 sm:py-2 bg-blue-600 text-white text-xs sm:text-sm rounded-lg hover:bg-blue-700 flex items-center justify-center"
            >
                <Plus className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5 sm:mr-2" />
                Ask a Doubt
            </button>
          </div>
        </div>
      </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 pb-3 sm:pb-4 pt-2">
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
            {/* Search */}
            <div className="flex-1">
        <div className="relative">
                <Search className="absolute left-2 sm:left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-3.5 h-3.5 sm:w-4 sm:h-4" />
          <input
            type="text"
            placeholder="Search doubts..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-8 sm:pl-10 pr-3 sm:pr-4 py-1.5 sm:py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                />
              </div>
            </div>

            {/* Filters */}
            <div className="flex gap-2">
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              >
                <option value="all">All Status</option>
                {statuses.filter(s => s !== 'all').map(status => (
                  <option key={status} value={status}>{status.charAt(0).toUpperCase() + status.slice(1)}</option>
                ))}
              </select>

              <select
                value={filterSubject}
                onChange={(e) => setFilterSubject(e.target.value)}
                className="px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              >
                <option value="all">All Subjects</option>
                {subjects.map(subject => (
                  <option key={subject} value={subject}>{subject}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Doubts List */}
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 pb-4 sm:pb-8 pt-2">
        {loading ? (
          <div className="flex items-center justify-center py-8 sm:py-12">
            <div className="animate-spin rounded-full h-6 w-6 sm:h-8 sm:w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : (
      <div className="space-y-3 sm:space-y-4">
            {filteredDoubts.map((doubt) => (
          <motion.div
            key={doubt.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
                className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 sm:p-6 hover:shadow-lg transition-shadow duration-200"
              >
                <div className="flex flex-col sm:items-start sm:justify-between gap-3 sm:gap-0 mb-3 sm:mb-4">
              <div className="flex-1 w-full">
                    <div className="flex flex-wrap items-center gap-2 sm:space-x-3 mb-2">
                      <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white flex-1 min-w-0">
                      {doubt.title}
                    </h3>
                      <span className={`px-2 py-1 text-[10px] sm:text-xs font-medium rounded-full flex items-center whitespace-nowrap ${getStatusColor(doubt.status)}`}>
                        {getStatusIcon(doubt.status)}
                        <span className="ml-1">{doubt.status.charAt(0).toUpperCase() + doubt.status.slice(1)}</span>
                    </span>
                    {doubt.status === 'answered' && doubt.answers && doubt.answers.some(answer => answer.isFromAdmin) && (
                      <span className="px-2 py-1 text-[10px] sm:text-xs bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400 rounded-full flex items-center whitespace-nowrap">
                        <CheckCircle className="w-2.5 h-2.5 sm:w-3 sm:h-3 mr-1" />
                        Admin Answered
                      </span>
                    )}
                </div>
                
                <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mb-2 sm:mb-3 line-clamp-2">
                      {doubt.description}
                </p>
                
                {/* Attachments */}
                    {doubt.attachments.length > 0 && (
                  <div className="mb-2 sm:mb-3">
                        <div className="flex items-center space-x-1.5 sm:space-x-2 text-xs sm:text-sm text-gray-500 dark:text-gray-400 mb-1.5 sm:mb-2">
                          <Paperclip className="w-3 h-3 sm:w-4 sm:h-4" />
                          <span>Attachments ({doubt.attachments.length})</span>
                        </div>
                    <div className="flex flex-wrap gap-1.5 sm:gap-2">
                          {doubt.attachments.map((attachment, index) => (
                            <div key={index} className="flex items-center space-x-1.5 sm:space-x-2 px-2 sm:px-3 py-1 bg-gray-100 dark:bg-gray-700 rounded-lg">
                              <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-300 truncate max-w-[120px] sm:max-w-none">{attachment.name}</span>
                              <span className="text-[10px] sm:text-xs text-gray-500 hidden sm:inline">({attachment.size})</span>
                              <button
                                onClick={() => window.open(attachment.url, '_blank')}
                                className="text-blue-600 hover:text-blue-800 flex-shrink-0"
                                title="View attachment"
                              >
                                <Eye className="w-3 h-3" />
                              </button>
                            </div>
                      ))}
                    </div>
                  </div>
                )}
                
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0 text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                  <div className="flex flex-wrap items-center gap-2 sm:space-x-4">
                        <div className="flex items-center space-x-1">
                          <User className="w-3 h-3 sm:w-4 sm:h-4" />
                          <span className="truncate">{doubt.author}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Clock className="w-3 h-3 sm:w-4 sm:h-4" />
                          <span>{formatTimestamp(doubt.createdAt, { includeTime: false, short: true })}</span>
                    </div>
                        <span className="px-1.5 sm:px-2 py-0.5 sm:py-1 bg-gray-100 dark:bg-gray-700 rounded-full text-[10px] sm:text-xs">
                        {doubt.subject}
                      </span>
                      <span className="text-xs sm:text-sm">
                          {doubt.answers.length} answer{doubt.answers.length !== 1 ? 's' : ''}
                        </span>
                    </div>
                      
                      <div className="flex items-center gap-2 flex-wrap">
                        <button
                          onClick={() => setSelectedDoubt(doubt)}
                          className="px-2 sm:px-3 py-1 text-[10px] sm:text-xs bg-blue-600 text-white rounded hover:bg-blue-700 whitespace-nowrap"
                        >
                          View Details
                        </button>
                        {doubt.author === 'Current User' && (
                          <button
                            onClick={() => handleDeleteDoubt(doubt)}
                            className="px-2 sm:px-3 py-1 text-[10px] sm:text-xs bg-red-600 text-white rounded hover:bg-red-700 flex items-center whitespace-nowrap"
                          >
                            <Trash2 className="w-2.5 h-2.5 sm:w-3 sm:h-3 mr-1" />
                            Delete
                          </button>
                        )}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
          </div>
        )}

        {!loading && filteredDoubts.length === 0 && (
          <div className="text-center py-8 sm:py-12">
            <MessageCircle className="w-10 h-10 sm:w-12 sm:h-12 text-gray-400 mx-auto mb-3 sm:mb-4" />
            <h3 className="text-base sm:text-lg font-medium text-gray-900 dark:text-white mb-2">No doubts found</h3>
            <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
              {searchTerm || filterStatus !== 'all' || filterSubject !== 'all'
                ? 'Try adjusting your search or filters' 
                : 'No doubts available yet'
              }
            </p>
          </div>
        )}
      </div>

      {/* Ask Doubt Modal */}
      {showAskModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-0 sm:p-4">
          <div className="bg-white dark:bg-gray-800 rounded-none sm:rounded-lg p-4 sm:p-6 max-w-2xl w-full h-full sm:h-auto sm:max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4 sm:mb-6">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">Ask a Doubt</h2>
              <button 
                onClick={() => {
                  setShowAskModal(false);
                  setUploadedFiles([]);
                }}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 flex-shrink-0 p-1"
              >
                <X className="w-5 h-5 sm:w-6 sm:h-6" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Subject
                </label>
                <select 
                  value={newDoubt.subject}
                  onChange={(e) => setNewDoubt({ ...newDoubt, subject: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                >
                  <option value="">Select Subject</option>
                  {subjects.map(subject => (
                    <option key={subject} value={subject}>{subject}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Title
                </label>
                <input
                  type="text"
                  value={newDoubt.title}
                  onChange={(e) => setNewDoubt({ ...newDoubt, title: e.target.value })}
                  placeholder="Brief title for your doubt"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Description
                </label>
                <textarea
                  rows={6}
                  value={newDoubt.description}
                  onChange={(e) => setNewDoubt({ ...newDoubt, description: e.target.value })}
                  placeholder="Describe your doubt in detail..."
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Attachments (Optional)
                </label>
                <FileUpload
                  onFilesUploaded={handleDoubtAttachments}
                  multiple={true}
                  maxFiles={5}
                  maxSize={10 * 1024 * 1024} // 10MB
                  storagePath="doubt-attachments"
                  showPreview={true}
                  showProgress={true}
                  showActions={true}
                  placeholder="Drag and drop files here, or click to select"
                  allowedTypes={[
                    'image/*', 
                    'application/pdf', 
                    'text/*', 
                    'application/*'
                  ]}
                />
              </div>
              </div>

            <div className="mt-4 sm:mt-6 flex flex-col sm:flex-row justify-end gap-2 sm:space-x-3">
              <button
                onClick={() => {
                  setShowAskModal(false);
                  setUploadedFiles([]);
                }}
                className="px-3 sm:px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={submitDoubt}
                disabled={submitting}
                className="px-3 sm:px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {submitting ? (
                  <>
                    <div className="animate-spin rounded-full h-3.5 w-3.5 sm:h-4 sm:w-4 border-b-2 border-white mr-1.5 sm:mr-2"></div>
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5 sm:mr-2" />
                    Submit Doubt
                  </>
                )}
              </button>
            </div>
          </div>
              </div>
      )}

      {/* Doubt Detail Modal */}
      {selectedDoubt && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-0 sm:p-4">
          <div className="bg-white dark:bg-gray-800 rounded-none sm:rounded-lg p-4 sm:p-6 max-w-4xl w-full h-full sm:h-auto sm:max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4 sm:mb-6">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">Doubt Details</h2>
                      <button
                onClick={() => setSelectedDoubt(null)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 flex-shrink-0 p-1"
                      >
                <X className="w-5 h-5 sm:w-6 sm:h-6" />
                      </button>
                    </div>

            <div className="space-y-4 sm:space-y-6">
              {/* Doubt Header */}
              <div className="border-b border-gray-200 dark:border-gray-700 pb-3 sm:pb-4">
                <div className="flex flex-wrap items-center gap-2 sm:space-x-3 mb-3">
                  <h3 className="text-base sm:text-xl font-semibold text-gray-900 dark:text-white flex-1 min-w-0">
                    {selectedDoubt.title}
                  </h3>
                  <span className={`px-2 sm:px-3 py-1 text-xs sm:text-sm font-medium rounded-full flex items-center whitespace-nowrap ${getStatusColor(selectedDoubt.status)}`}>
                    {getStatusIcon(selectedDoubt.status)}
                    <span className="ml-1">{selectedDoubt.status.charAt(0).toUpperCase() + selectedDoubt.status.slice(1)}</span>
                  </span>
                </div>
                
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  {selectedDoubt.description}
                </p>

                {/* Attachments */}
                {selectedDoubt.attachments.length > 0 && (
                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Attachments</h4>
                    <div className="space-y-2">
                      {selectedDoubt.attachments.map((attachment, index) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                          <div className="flex items-center space-x-3">
                            <Paperclip className="w-4 h-4 text-gray-400" />
                            <div>
                              <p className="text-sm font-medium text-gray-900 dark:text-white">{attachment.name}</p>
                              <p className="text-xs text-gray-500">{attachment.size}</p>
                </div>
              </div>
                          <div className="flex items-center space-x-2">
                <button
                              onClick={() => window.open(attachment.url, '_blank')}
                              className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center"
                >
                              <Eye className="w-3 h-3 mr-1" />
                              View
                </button>
                <BookmarkButton
                  itemId={`${selectedDoubt.id}-attachment-${index}`}
                  type="resource"
                  title={attachment.name}
                  description={`Attachment from doubt: ${selectedDoubt.title}`}
                  url={attachment.url}
                  thumbnail=""
                />
              </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400">
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-1">
                      <User className="w-4 h-4" />
                      <span>{selectedDoubt.author}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Clock className="w-4 h-4" />
                      <span>{formatTimestamp(selectedDoubt.createdAt, { includeTime: false, short: true })}</span>
                    </div>
                    <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded-full">
                      {selectedDoubt.subject}
                    </span>
                  </div>
                  {selectedDoubt.author === 'Current User' && (
                    <button
                      onClick={() => handleDeleteDoubt(selectedDoubt)}
                      className="px-3 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700 flex items-center"
                    >
                      <Trash2 className="w-3 h-3 mr-1" />
                      Delete Doubt
                    </button>
                  )}
                </div>
              </div>

              {/* Answers */}
              <div>
                <h4 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-3 sm:mb-4">
                  Answers ({selectedDoubt.answers.length})
                </h4>
                
                {selectedDoubt.answers.length === 0 ? (
                  <div className="text-center py-6 sm:py-8">
                    <MessageCircle className="w-10 h-10 sm:w-12 sm:h-12 text-gray-400 mx-auto mb-3 sm:mb-4" />
                    <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">No answers yet. Experts will respond soon.</p>
                  </div>
                ) : (
                  <div className="space-y-3 sm:space-y-4">
                    {selectedDoubt.answers.map((answer) => (
                      <div key={answer.id} className={`border rounded-lg p-3 sm:p-4 ${
                        answer.isFromAdmin 
                          ? 'border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-900/20' 
                          : 'border-gray-200 dark:border-gray-700'
                      }`}>
                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 sm:gap-0 mb-2 sm:mb-3">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-medium text-sm sm:text-base text-gray-900 dark:text-white">{answer.author}</span>
                            {answer.isFromAdmin && (
                              <span className="px-1.5 sm:px-2 py-0.5 sm:py-1 text-[10px] sm:text-xs bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400 rounded-full">
                                Admin
                              </span>
                            )}
                            {answer.isAccepted && (
                              <span className="px-1.5 sm:px-2 py-0.5 sm:py-1 text-[10px] sm:text-xs bg-green-100 text-green-800 rounded-full">
                                Accepted
                              </span>
                            )}
                          </div>
                          <span className="text-xs sm:text-sm text-gray-500">{formatTimestamp(answer.createdAt, { includeTime: false, short: true })}</span>
                        </div>
                        <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mb-3">{answer.content}</p>
                        
                        {/* Answer Attachments/Images */}
                        {answer.attachments && answer.attachments.length > 0 && (
                          <div className="mt-3 space-y-2">
                            <div className="flex flex-wrap gap-2">
                              {answer.attachments.map((attachment, attIndex) => (
                                <div key={attIndex} className="relative group">
                                  {attachment.type?.startsWith('image/') || attachment.url?.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
                                    <div className="relative">
                                      <img
                                        src={attachment.url}
                                        alt={`Answer image ${attIndex + 1}`}
                                        className="max-w-xs max-h-48 rounded-lg border border-gray-300 dark:border-gray-600 cursor-pointer hover:opacity-90 transition-opacity"
                                        onClick={() => window.open(attachment.url, '_blank')}
                                      />
                                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-opacity rounded-lg flex items-center justify-center">
                                        <Eye className="w-5 h-5 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                                      </div>
                                    </div>
                                  ) : (
                                    <div className="flex items-center space-x-2 px-3 py-2 bg-gray-100 dark:bg-gray-700 rounded-lg">
                                      <Paperclip className="w-4 h-4 text-gray-400" />
                                      <span className="text-sm text-gray-600 dark:text-gray-300 truncate max-w-[200px]">
                                        {attachment.name || `Attachment ${attIndex + 1}`}
                                      </span>
                                      <button
                                        onClick={() => window.open(attachment.url, '_blank')}
                                        className="text-blue-600 hover:text-blue-800 dark:text-blue-400"
                                        title="View attachment"
                                      >
                                        <Eye className="w-4 h-4" />
                                      </button>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && doubtToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 sm:p-6 max-w-md w-full">
            <div className="flex items-center justify-between mb-3 sm:mb-4">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">Delete Doubt</h3>
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setDoubtToDelete(null);
                }}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 flex-shrink-0 p-1"
              >
                <X className="w-5 h-5 sm:w-6 sm:h-6" />
              </button>
            </div>
            
            <div className="mb-4 sm:mb-6">
              <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mb-3 sm:mb-4">
                Are you sure you want to delete this doubt? This action cannot be undone and will also delete all answers.
              </p>
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 sm:p-4">
                <h4 className="font-medium text-sm sm:text-base text-gray-900 dark:text-white mb-1.5 sm:mb-2">{doubtToDelete.title}</h4>
                <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 line-clamp-2">{doubtToDelete.description}</p>
                <div className="flex flex-wrap items-center gap-2 sm:space-x-4 mt-2 text-[10px] sm:text-xs text-gray-500">
                  <span>Subject: {doubtToDelete.subject}</span>
                  <span>Answers: {doubtToDelete.answers ? doubtToDelete.answers.length : 0}</span>
                </div>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row justify-end gap-2 sm:space-x-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setDoubtToDelete(null);
                }}
                className="px-3 sm:px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={deleteDoubt}
                disabled={deleting}
                className="px-3 sm:px-4 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {deleting ? (
                  <>
                    <div className="animate-spin rounded-full h-3.5 w-3.5 sm:h-4 sm:w-4 border-b-2 border-white mr-1.5 sm:mr-2"></div>
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5 sm:mr-2" />
                    Delete Doubt
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* AI Tutor Info Modal (When Locked) */}
      {showAiTutorInfo && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-full flex items-center justify-center">
                  <Lock className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                </div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">AI Tutor - Coming Soon</h2>
              </div>
              <button 
                onClick={() => setShowAiTutorInfo(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4 text-gray-800 dark:text-gray-200">
              <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-400 p-4 rounded">
                <div className="flex items-start">
                  <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 mr-3 mt-0.5 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-blue-900 dark:text-blue-300 mb-2">AI Tutor Feature</h3>
                    <p className="text-blue-800 dark:text-blue-200 text-sm">
                      The AI Tutor feature is currently locked and will be unlocked by the administrator. Once unlocked, you will have access to a powerful AI-powered learning assistant.
                    </p>
                  </div>
                </div>
              </div>

              <div className="border-b pb-4">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">What AI Tutor Will Offer:</h3>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start">
                    <span className="text-purple-600 dark:text-purple-400 mr-2">•</span>
                    <span><strong>Solving Your Doubts:</strong> Get instant answers to your academic questions in Hinglish, Hindi, or English</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-purple-600 dark:text-purple-400 mr-2">•</span>
                    <span><strong>Doubt Classes:</strong> Interactive doubt-solving sessions with step-by-step explanations</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-purple-600 dark:text-purple-400 mr-2">•</span>
                    <span><strong>PTM (Parent-Teacher Meetings):</strong> AI-assisted preparation and insights for parent-teacher interactions</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-purple-600 dark:text-purple-400 mr-2">•</span>
                    <span><strong>Introduction Sessions:</strong> Personalized introduction and orientation sessions for new topics</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-purple-600 dark:text-purple-400 mr-2">•</span>
                    <span><strong>Test Analysis Live Classes:</strong> Detailed analysis of your test performance with live interactive sessions</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-purple-600 dark:text-purple-400 mr-2">•</span>
                    <span><strong>And Much More:</strong> Voice interaction, image/PDF upload support, whiteboard for math solutions, and personalized learning assistance</span>
                  </li>
                </ul>
              </div>

              <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded">
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  <strong>Note:</strong> This feature will be unlocked by the administrator. Please check back later or contact support for more information.
                </p>
              </div>

              <div className="flex justify-end">
                <button
                  onClick={() => setShowAiTutorInfo(false)}
                  className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MahStry AI Tutor Modal */}
      <MahStryAITutor
        isOpen={showMahStryAITutor}
        onClose={() => setShowMahStryAITutor(false)}
        initialSubject={filterSubject !== 'all' ? filterSubject : 'General'}
      />
    </div>
  );
};

export default Doubts;
